import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { Card, Col, Row, Spinner } from "react-bootstrap";
import DepartmentTopbar from "./components/DepartmentTopbar";
import DepartmentSidebar from "./components/DepertmentSidebar";
import styles from "../../assets/css/Dashboard.module.css";
import { FaUsers, FaRegThumbsUp, FaCheckCircle, FaToilet, FaWater } from "react-icons/fa";
import CashDashboard from "./CashDashboard";

// Reusable Stat Card Component for HFA
const HfaStatCard = ({ title, value, icon, color, onClick }) => (
  <Col md={4} className="mb-4">
    <Card
      className="shadow-sm h-100"
      onClick={onClick}
      style={{ cursor: "pointer", borderLeft: `5px solid ${color}` }}
    >
      <Card.Body className="d-flex justify-content-between align-items-center">
        <div>
          <h6 className="text-muted mb-1" style={{ color: color }}>
            {title}
          </h6>
          <h3 className="fw-bold mb-0">{value}</h3>
        </div>
        <div
          className="icon-circle"
          style={{ backgroundColor: `${color}20`, color: color }}
        >
          {icon}
        </div>
      </Card.Body>
    </Card>
  </Col>
);

// Reusable Pending Installment Card for HFA
const PendingCard = ({ title, value, color }) => (
    <Col md={3} className="mb-4">
        <Card className="text-center shadow-sm h-100">
            <Card.Body>
                <h5 className="fw-bold" style={{color: color}}>{value}</h5>
                <p className="text-muted mb-0">{title}</p>
            </Card.Body>
        </Card>
    </Col>
);


function DepartmentDashboard() {
  const navigate = useNavigate();
  const [pwdReportSummary, setPwdReportSummary] = useState({
    total: 0,
    pending: 0,
    complete: 0,
    loading: true,
    error: null,
  });

  const [departmentType, setDepartmentType] = useState("");
  
  // Get user information for filtering
  const user_id = localStorage.getItem("user_id");
  const userRole = localStorage.getItem("role");
  const isSuperAdmin = userRole === "Super_Admin";
  
  // Filtering functions (same as in pwdreportlist.js)
  const matchesLoggedInUser = (item) => {
    const concernedId =
      item?.name_of_concerned_id ?? item?.name_of_concerned ?? "";
    return String(concernedId) === String(user_id ?? "");
  };
  
  const filterDataForUser = (data) => {
    if (!Array.isArray(data)) return [];
    const userType = localStorage.getItem("type");
    if (String(userType).trim() === "SAE") {
      return data.filter((item) => matchesLoggedInUser(item));
    }
    return data;
  };
  
  const [hfaStats, setHfaStats] = useState({
    total_beneficiaries: 0,
    installments: {},
    loading: true,
  });

  useEffect(() => {
    const type = localStorage.getItem("type");
    setDepartmentType(type);

    const fetchPWDReportSummary = async () => {
        setPwdReportSummary((prev) => ({ ...prev, loading: true, error: null }));
        try {
          const token = localStorage.getItem("token");
          const response = await fetch(
            `${process.env.REACT_APP_SUPER_ADMIN_API_URL}/pwd-list.php`,
            {
              method: "GET",
              headers: {
                Authorization: `Bearer ${token}`,
                "Content-Type": "application/json",
              },
            }
          );
          const data = await response.json();
          
          if (Array.isArray(data)) {
            // Apply user-based filtering
            const filteredData = filterDataForUser(data);
            
            const total = filteredData.length;
            const pending = filteredData.filter(
              (item) =>
                (item.final_status || "").toLowerCase().trim() === "pending"
            ).length;
            const complete = filteredData.filter(
              (item) =>
                (item.final_status || "").toLowerCase().trim() === "complete"
            ).length;
            setPwdReportSummary({
              total,
              pending,
              complete,
              loading: false,
              error: null,
            });
          } else {
            setPwdReportSummary({ total: 0, pending: 0, complete: 0, loading: false, error: "Invalid data" });
          }
        } catch (error) {
          setPwdReportSummary({ total: 0, pending: 0, complete: 0, loading: false, error: error.message });
        }
      };

    const fetchHfaDashboardData = async () => {
        setHfaStats(prev => ({ ...prev, loading: true }));
        try {
          const token = localStorage.getItem("token");
          const response = await axios.get(
            `${process.env.REACT_APP_SUPER_ADMIN_API_URL}/hfa-dashboard.php`,
            {
              headers: { Authorization: `Bearer ${token}` },
            }
          );
          
          if (response.data.success) {
            setHfaStats({
              total_beneficiaries: response.data.total_beneficiaries,
              installments: response.data.installments,
              loading: false,
            });
          } else {
             setHfaStats(prev => ({...prev, loading: false}));
          }
        } catch (error) {
          console.error("Failed to fetch HFA dashboard data", error);
          setHfaStats(prev => ({ ...prev, loading: false }));
        }
      };

    if (type === "HFA") {
        fetchHfaDashboardData();
    } else {
        fetchPWDReportSummary();
    }
  }, []);

  const sanitaryMenuItems = [
    {
      name: "Water Tank",
      icon: <FaWater />,
      path: "/departmentDashboard/sanitary",
    },
    {
      name: "Sanitary",
      icon: <FaToilet />,
      path: "/departmentDashboard/water-tank",
    },
  ];

  const handleCardClick = (path) => {
    navigate(path);
  };

  const renderDashboardContent = () => {
    if (departmentType === "HFA") {
        if (hfaStats.loading) {
            return <div className="text-center p-5"><Spinner animation="border" /></div>;
        }
        return (
            <>
              <Row>
                <HfaStatCard
                  title="Total Beneficiaries"
                  value={hfaStats.total_beneficiaries}
                  icon={<FaUsers />}
                  color="#0d6efd"
                  onClick={() => navigate("/departmentDashboard/hfa-beneficiary-list")}
                />
                <HfaStatCard
                  title="1st Installment Paid"
                  value={hfaStats.installments['1st_installment']?.paid || 0}
                  icon={<FaRegThumbsUp />}
                  color="#198754"
                  onClick={() => navigate("/departmentDashboard/hfa-beneficiary-list")}
                />
                <HfaStatCard
                  title="Fully Paid (5th Inst.)"
                  value={hfaStats.installments['5th_installment']?.paid || 0}
                  icon={<FaCheckCircle />}
                  color="#ffc107"
                  onClick={() => navigate("/departmentDashboard/hfa-beneficiary-list")}
                />
              </Row>
              <hr className="my-4"/>
              <h4 className="mb-3">Pending Installments</h4>
              <Row>
                <PendingCard title="Ready for 2nd Installment" value={hfaStats.installments['1st_installment']?.paid - hfaStats.installments['2nd_installment']?.paid || 0} color="#dc3545" />
                <PendingCard title="Ready for 3rd Installment" value={hfaStats.installments['2nd_installment']?.paid - hfaStats.installments['3rd_installment']?.paid || 0} color="#fd7e14" />
                <PendingCard title="Ready for 4th Installment" value={hfaStats.installments['3rd_installment']?.paid - hfaStats.installments['4th_installment']?.paid || 0} color="#20c997" />
                <PendingCard title="Ready for 5th Installment" value={hfaStats.installments['4th_installment']?.paid - hfaStats.installments['5th_installment']?.paid || 0} color="#0dcaf0" />
              </Row>
            </>
        );
    } else if (departmentType === "Sanitary") {
        return (
            <div className={styles.cardGrid}>
              {sanitaryMenuItems.map((item, index) => (
                <div
                  key={index}
                  className={styles.card}
                  onClick={() => handleCardClick(item.path)}
                >
                  <div className={styles.iconWrapper}>{item.icon}</div>
                  <div className={styles.cardContent}>
                    <div className={styles.cardTitle}>{item.name}</div>
                  </div>
                </div>
              ))}
            </div>
        );
    } else if (departmentType === "Cash") {
     return (
      <CashDashboard />
     )
    }else {
        // Default PWD Dashboard
        return (
            <div className={styles.cardGrid}>
                {/* PWD Cards */}
                <div className={styles.card}>
                    <div className={styles.cardContent}>
                        <h3 className={styles.cardTitle}>Total PWD Reports</h3>
                        <p className={styles.cardDescription}>All PWD Reports</p>
                        {pwdReportSummary.loading ? <div>Loading...</div> : <div className={styles.statsValue}>{pwdReportSummary.total ?? 0}</div>}
                    </div>
                </div>
                <div className={styles.card}>
                    <div className={styles.cardContent}>
                        <h3 className={styles.cardTitle}>Pending PWD Reports</h3>
                        <p className={styles.cardDescription}>Pending Status</p>
                        {pwdReportSummary.loading ? <div>Loading...</div> : <div className={styles.statsValue} style={{color: '#f44336'}}>{pwdReportSummary.pending ?? 0}</div>}
                    </div>
                </div>
                <div className={styles.card}>
                    <div className={styles.cardContent}>
                        <h3 className={styles.cardTitle}>Completed PWD Reports</h3>
                        <p className={styles.cardDescription}>Completed Status</p>
                        {pwdReportSummary.loading ? <div>Loading...</div> : <div className={styles.statsValue} style={{color: '#4caf50'}}>{pwdReportSummary.complete ?? 0}</div>}
                    </div>
                </div>
            </div>
        );
    }
  };

  return (
    <>
      <DepartmentTopbar />
      <div className="d-flex" style={{ minHeight: "calc(100vh - 56px)" }}>
        <DepartmentSidebar />
        <div className="flex-grow-1 p-4 bg-light">
          <div className={styles.dashboardContent}>
            <h2 className="mb-4">Department Dashboard ({departmentType})</h2>
            {renderDashboardContent()}
          </div>
        </div>
      </div>
    </>
  );
}

export default DepartmentDashboard;

